"use client";
import Image from "next/image";
import { memo, useCallback, useEffect, useState } from "react";
import { CircleChevronLeft, CircleChevronRight } from "lucide-react";
import * as images from "@/components/images/Images";
import { Typography } from "@/components/ui/typography";
import Carousel, { CarouselSlide } from "@/components/ui/carousel";
import type { KeenSliderInstance } from "keen-slider";
import { useSliderEdges } from "@/components/ui/carousel/hooks/useCarouselEdges";
import StarRating from "@/components/ui/star-rating";
import {
  TRUSTPILOT_RATING_DISPLAY,
  TRUSTPILOT_REVIEW_COUNT,
  TRUSTPILOT_REVIEW_URL,
} from "@/components/screen/start-order/components/Checkout/constants";
import { trustpilotTestimonials } from "@/data/trustpilotTestimonials";

const sliderOptions = {
  loop: true,
  slides: {
    perView: 1,
    spacing: 35,
  },
  breakpoints: {
    "(min-width: 768px)": {
      slides: {
        perView: 2,
        spacing: 35,
      },
    },
    "(min-width: 1024px)": {
      slides: {
        perView: 3,
        spacing: 35,
      },
    },
  },
};

// 2026-05-25 — testimonials moved to a shared source-of-truth
// module so Home, Urgent, and Quality-guarantee all stay in sync.
// To refresh review content (or expand the set), edit only
// `src/data/trustpilotTestimonials.ts`. Sourcing rules and the
// "verbatim from live Trustpilot" guardrail live in that file.
const testimonials = trustpilotTestimonials;

const Excellence = () => {
  const [sliderReady, setSliderReady] = useState<KeenSliderInstance | null>(
    null
  );
  const { isBeginning, isEnd, canScroll, updateEdges } =
    useSliderEdges(sliderReady);

  useEffect(() => {
    if (!sliderReady) {
      return;
    }

    sliderReady.on("updated", (s) => {
      updateEdges(s);
    });
  }, [sliderReady]);

  const goToPrevSlide = useCallback(() => {
    sliderReady?.prev();
  }, [sliderReady]);

  const goToNextSlide = useCallback(() => {
    sliderReady?.next();
  }, [sliderReady]);

  return (
    <section className="section">
      <div className="container mx-auto py-12 lg:pt-[82] lg:pb-[3.25rem]">
        <div className="flex flex-col justify-center gap-4">
          <Typography variant="heading4" className="text-center">
            <h2>Languex is language excellence</h2>
          </Typography>
          <Typography
            variant="subHeadingSm"
            className="text-center text-gray-700"
            asChild
          >
            <p>
              Highest in-market TrustPilot rating for human translation across
              thousands of reviews
            </p>
          </Typography>
        </div>
        <div className="flex justify-center mt-4 sm:justify-start sm:items-start mt-6 md:mt-[30]">
          <div className="flex flex-col gap-2 sm:gap-2.5 items-center shrink-0 w-full max-w-[215] sm:w-auto sm:max-w-none sm:mr-[30]">
            <span className="text-[18px] leading-[26px] sm:text-2xl sm:mt-2.5">
              Excellent
            </span>
            {/*
              2026-05-25 — rating, count, and target URL all moved off
              the hard-coded "4.5 / 195 reviews" placeholders onto the
              shared TRUSTPILOT_* constants so this card stays in sync
              with the Cost Calculator, /get-quote confirmation, and
              the home JSON-LD. Wrapped the count in an anchor to the
              real Trustpilot review page so clicking it goes
              somewhere instead of nowhere — matches how Trustpilot's
              own embeds behave.
            */}
            <StarRating size="md" rating={Number(TRUSTPILOT_RATING_DISPLAY)} />
            <span className="text-[14px] leading-[22px] sm:text-[12px] sm:leading-[22px]">
              Based on{" "}
              <a
                href={TRUSTPILOT_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-semibold lg:font-normal hover:text-blue-500"
              >
                {TRUSTPILOT_REVIEW_COUNT.toLocaleString()} reviews
              </a>
            </span>
            <a
              href={TRUSTPILOT_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-end mr-1 sm:mr-0 sm:mt-1 hover:opacity-80 transition-opacity"
              aria-label="View Languex reviews on Trustpilot"
            >
              <Image
                alt="trustpilot"
                src={images.trustpilot}
                width={24}
                height={0}
                loading="lazy"
                className="shrink-0 sm:-mt-1"
              />
              <span className="font-medium">Trustpilot</span>
            </a>
          </div>
          <div className="relative hidden items-center flex-auto min-w-0 sm:flex">
            <button
              onClick={goToPrevSlide}
              className="z-10 text-gray-300 mr-[30] cursor-pointer relative font-normal outline-none inline-flex items-center justify-center select-none touch-manipulation transition-colors text-semantic-text-primary duration-200 shrink-0 disabled:text-semantic-foreground-weak disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full"
              // disabled={isBeginning}
              aria-disabled={isBeginning}
              aria-label="Previous testimonial"
              title="Previous testimonial"
            >
              <CircleChevronLeft width={28} height={28} strokeWidth={1} />
            </button>

            <Carousel getSlider={setSliderReady} options={sliderOptions}>
              {testimonials.map((item, index) => (
                <CarouselSlide key={index} className="pt-2.5 !h-auto">
                  <div className="bg-transparent lg:h-40 flex flex-col gap-2 items-center justify-start text-xl font-bold rounded">
                    <div className="flex w-full justify-between">
                      <div className="flexl">
                        <StarRating className="[&>span]:size-[21.5px] [&_svg]:size-[15px]" />
                      </div>
                      <div className="flex justify-end items-end">
                        <span className="text-gray-800 flex text-[12px] font-normal">
                          {item.date}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col font-normal flex-auto">
                      <h3 className="font-medium text-sm mb-2">{item.title}</h3>
                      <p className="text-[12px] mb-2">{item.text}</p>
                      <p className="text-[12px] text-gray-800 mt-auto pt-[20]">
                        {item.name}
                      </p>
                    </div>
                  </div>
                </CarouselSlide>
              ))}
            </Carousel>

            <button
              onClick={goToNextSlide}
              className="z-10 text-gray-300 ml-[30] cursor-pointer relative font-normal outline-none inline-flex items-center justify-center select-none touch-manipulation transition-colors text-semantic-text-primary duration-200 shrink-0 disabled:text-semantic-foreground-weak disabled:cursor-not-allowed focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded-full"
              disabled={isEnd}
              aria-disabled={isEnd}
              aria-label="Next testimonial"
              title="Next testimonial"
            >
              <CircleChevronRight width={28} height={28} strokeWidth={1} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default memo(Excellence);
